import type { ReactNode } from 'react';

export interface FormFieldProps {
  label: ReactNode;
  children: ReactNode;
  /** Draws the required mark; the control still owns its own validation. */
  required?: boolean;
  /** A line under the control — a format, a consequence. */
  hint?: ReactNode;
  /** A validation message; replaces the hint while present. */
  error?: ReactNode;
}

/**
 * One labelled field, for the drawers and panels that are not an AntD `Form`.
 *
 * The session create/edit drawers and the photo upload drawer each carried a
 * private copy (12px subtle label), the consent drawers used a 14px secondary
 * `Text` with no gap, and the settings panels a third style — three label
 * treatments in one app. This is the one: a real `<label>` wrapping the
 * control, the `.bk-field-label` style shared with the settings forms (the
 * same size, colour and 8px gap as AntD's vertical form labels), a required
 * mark, and a hint or error slot beneath.
 */
export function FormField({ label, children, required = false, hint, error }: FormFieldProps) {
  return (
    <label className="bk-form-field">
      <span className="bk-field-label">
        {label}
        {required ? (
          <span className="bk-field-label__required" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </span>
      {children}
      {error ? (
        <span className="bk-form-field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="bk-form-field__hint">{hint}</span>
      ) : null}
    </label>
  );
}
